"use strict";

import { RequestUtil } from './common-util';

export interface WebDisplayJsonPayload {
  webDisplayInitializationData: any;
  type: string;
  cssUrl: string;
  clipboardCopyAllowed: boolean;
  card: { issuerCardId: string };
  lifetime: number;
  ipAddresses?: string[];
}

export class WebSecureDisplayClient {
  static PIN_CODE = "PIN_CODE";
  static CARD_DETAIL = "CARD_DETAILS";
  static CARD_DETAILS_AND_STATIC_CRYPTOGRAM =
    "CARD_DETAILS_AND_STATIC_CRYPTOGRAM";

  private apiUrl: string;
  private xIssuerId: string;
  private xUserId: string;
  private token: string;
  private cssUrl: string;
  private targetOrigin: string;
  private lifetime: number;
  private enableCopy: boolean;
  private webSecureDisplaysURL: string;
  private iframeURL: string;
  private iframe: any;
  private channel: any;
  public onDisplaySuccess: () => void;
  public onDisplayError: () => void;

  constructor(apiUrl: string, xIssuerId: string, xUserId: string, token: string, cssUrl: string, lifetime: number, enableCopy?: boolean) {
    this.apiUrl = apiUrl;
    this.xIssuerId = xIssuerId;
    this.xUserId = xUserId;
    this.token = token;
    this.cssUrl = cssUrl;
    this.targetOrigin = apiUrl;
    this.lifetime = lifetime;
    this.enableCopy = enableCopy ?? false;
    this.webSecureDisplaysURL = "/api/v1/dcs/cards/webSecureDisplays";
    this.iframeURL = "/api/v1/sca/webSecureDisplays/iframe";
    this.iframe = "";
    this.channel = "";
    //Allow only onDisplaySuccess and onDisplayError function to be overided
    Object.defineProperty(this, "onDisplaySuccess", {
      writable: true,
      configurable: true,
      value: () => {
        return "";
      },
    });
    Object.defineProperty(this, "onDisplayError", {
      writable: true,
      configurable: true,
      value: () => {
        return "";
      },
    });
    Object.preventExtensions(this);
  }

  initializeIframe(containerId: string, type: string, issuerCardId: string, ipAddress?: string): void {
    this._initializeIframe(containerId, type, issuerCardId, ipAddress);
  }

  initializeIframeWithError(containerId: string, type: string, issuerCardId: string): void {
    //Make
    this.lifetime = 2;
    this._initializeIframe(containerId, type, issuerCardId, undefined, 2000);
  }

  private _initializeIframe(containerId: string, type: string, issuerCardId: string, ipAddress?: string, waitTime?: number): void {
    // Create iframe
    this.createIframe(containerId);
    //Initialize communication channel
    this.createChannel(type, issuerCardId, ipAddress, waitTime);
    // Manage event on iframe load
    this.iframe.addEventListener("load", () => this.onIframeLoad());
    //Add iframe to dom
    this.addIframe(containerId);
  }

  // Send the initialization message to the iframe once it's loaded
  private onIframeLoad(): void {
    this.iframe.contentWindow.postMessage(
      "init",
      // Target only the iframe with the correct origin
      this.targetOrigin,
      // Transfer the 2nd port to the iframe so it can send back messages
      [this.channel.port2]
    );
  }

  //Add iframe to DOM
  private addIframe(containerId: string): void {
    // Don't forget to clean up! TODO FIX THIS
    //controller.$onDestroy = channel.port1.close.bind(channel.port1);
    //Attach ifrme to DOM
    const element = document.getElementById(containerId);
    const src = this.getIframePath();
    this.iframe.setAttribute("src", src);
    //Clear previous container content
    element.innerHTML = "";
    element.append(this.iframe);
  }

  // Create iframe without add it to the dom.
  private createIframe(containerId: string): void {
    this.iframe = document.createElement("iframe");
    this.iframe.setAttribute("id", containerId + "-iframe");
    this.iframe.setAttribute("class", "secure-display-iframe");
    //Authorize iframe to use clipboard
    this.iframe.setAttribute("allow", "clipboard-write");
  }

  private createChannel(type: string, issuerCardId: string, ipAddress?: string, waitTime?: number): void {
    this.channel = new MessageChannel();
    // First port will update the app through AngularJS output bindings
    this.channel.port1.onmessage = (event) => {
      const isWebDisplayInitializationData =
        this.isWebDisplayInitializationData(event);
      if (!!isWebDisplayInitializationData) {
        const webDisplayJson: WebDisplayJsonPayload = {
          webDisplayInitializationData: event.data,
          type,
          cssUrl: this.cssUrl,
          clipboardCopyAllowed: this.enableCopy,
          card: { issuerCardId },
          lifetime: this.lifetime,
        };
        if (!!ipAddress) {
          webDisplayJson.ipAddresses = [ipAddress];
        }
        this.postWeDisplayInitialization(webDisplayJson).then((data) => {
          if (data && data.webDisplayOpaqueData) {
            if (!!waitTime) {
              setTimeout(() => {
                this.sendWebDisplayDataToIframe(data);
              }, waitTime);
            } else {
              this.sendWebDisplayDataToIframe(data);
            }
          } else {
            this.onDisplayError();
          }
        });
      } else if (event.data === "displaySuccess") {
        this.onDisplaySuccess();
      } else if (event.data === "displayError") {
        this.onDisplayError();
      }
    };
  }

  //Generate the complete URL for Iframe
  private getIframePath(): string {
    const queryString = `X-Issuer-Id=${this.xIssuerId}&X-User-Id=${this.xUserId}`;
    return `${this.apiUrl}${this.iframeURL}?${encodeURI(queryString)}`;
  }

  //Verify the webdisplay initialization data type
  private isWebDisplayInitializationData(event: MessageEvent): boolean {
    return typeof event.data === "string" && event.data.startsWith("01.");
  }

  //Send request to the backend in order to ini
  private postWeDisplayInitialization(data: any): Promise<any> {
    return RequestUtil.postData(
      this.apiUrl + this.webSecureDisplaysURL,
      data,
      this.xIssuerId,
      this.xUserId,
      this.token
    );
  }

  private sendWebDisplayDataToIframe(data: any): void {
    //Send webDisplayOpaqueData to iframe
    const webDisplayOpaqueData = data.webDisplayOpaqueData;
    this.iframe.contentWindow.postMessage(
      { xIssuerId: this.xIssuerId, webDisplayOpaqueData },
      this.targetOrigin
    );
  }
}
